<template>
  <div class="mx-10 ma-5">
    <v-layout row v-if="errors">
      <v-flex>
        <Alert @dismissed="onDismissed" :text="errors"></Alert>
      </v-flex>
    </v-layout>
    <div>
      <v-card class="pa-5">
        <PageTitleWithSearch
          :title="'Customers'"
          :btnName="'Add New Customer'"
          :btnColor="'#00B3FF'"
          :btnRoute="'/customers/create'"
          :btnPermission="'customer.create'"
          :btnEnable="true"
          :loading="loading"
          :search="search"
          @searchText="searchChange($event)"
        >
        </PageTitleWithSearch>
        <DataTableWithPagination
          :headers="headers"
          :items="customers"
          :loading="loading"
          :page="page"
          :total="total"
          @pageChange="page = $event"
        >
          <template #row="{ item}">
            <tr :key="item.uuid">
              <td>
                <v-avatar size="36px">
                  <img v-if="item.logo" :src="item.logo" alt="Customer Logo" />
                  <img v-else :src="defaultImage" alt="Default Logo" />
                </v-avatar>
              </td>
              <td class="text-left">
                <template v-if="hasPermission('customer.view')">
                  <router-link
                    :to="'/customer/' + item.uuid + '/info'"
                    class="router-link"
                  >
                    {{ item.customer_code }}
                  </router-link>
                </template>
                <template v-else>
                  {{ item.customer_code }}
                </template>
              </td>
              <td class="text-left">{{ item.company_name }}</td>
              <td class="text-left">
                <span v-if="item.phone">{{ item.phone }}</span>
                <span v-else>-</span>
              </td>
              <td class="text-left">
                <span v-if="item.email">{{ item.email }}</span>
                <span v-else>-</span>
              </td>
              <td class="text-left">
                <span v-if="item.mobile">{{ item.mobile }}</span>
                <span v-else>-</span>
              </td>
              <td class="text-left">
                <span v-if="item.city || item.state || item.country">
                  {{
                    [item.city, item.state, item.country]
                      .filter(Boolean)
                      .join(", ")
                  }}
                </span>
                <span v-else>-</span>
              </td>
              <td class="text-right">
                <v-btn
                  v-if="hasPermission('customer.edit')"
                  color="#FFE0B2"
                  :to="'/customers/edit/' + item.uuid"
                  fab
                  small
                  dark
                  style="box-shadow: none;"
                >
                  <v-icon small color="#FF9800"> mdi-pencil </v-icon>
                </v-btn>
                <v-btn v-else disabled fab small dark style="box-shadow: none;">
                  <v-icon small color="#FF9800"> mdi-pencil </v-icon>
                </v-btn>
              </td>
              <td class="text-left">
                <v-btn
                  v-if="hasPermission('customer.delete')"
                  small
                  color="#FFCDD2"
                  @click="dropMemberConfirm(item.uuid)"
                  fab
                  style="box-shadow: none;"
                >
                  <v-icon dark small color="#F44336">
                    mdi-delete
                  </v-icon>
                </v-btn>
                <v-btn v-else disabled fab small style="box-shadow: none;">
                  <v-icon dark small color="#F44336">
                    mdi-delete
                  </v-icon>
                </v-btn>
              </td>
            </tr>
          </template>
        </DataTableWithPagination>
      </v-card>
    </div>

    <ConfirmModal
      :text="'Do you want to drop this customer?'"
      :title="'Drop Customer'"
      :color="'red'"
      :confirmOpen="confirmOpen"
      @cancelConfirm="cancelConfirm()"
      @confirm="confirm()"
    ></ConfirmModal>
    <Snackbars
      :text="successMsg"
      :snackbar="snackbar"
      :color="'warning'"
      @classSnackbar="closeSnackbar"
    />
  </div>
</template>

<script>
import { createNamespacedHelpers } from "vuex";
const { mapGetters } = createNamespacedHelpers("auth");

import PageTitleWithSearch from "@/components/shared/PageTitleWithSearch";
import ConfirmModal from "@/components/shared/ConfirmModal";
import Snackbars from "@/components/shared/Snackbars";
import DataTableWithPagination from "@/components/shared/DataTableWithPagination.vue";
import { CustomerService } from "@/services/customer-service.js";
import { checkError } from "@/config/errors.js";
import Alert from "@/components/shared/Alert.vue";

export default {
  name: "CustomerGrid",
  components: {
    PageTitleWithSearch,
    ConfirmModal,
    Snackbars,
    DataTableWithPagination,
    Alert,
  },
  data: function() {
    return {
      customers: [],
      page: 1,
      search: "",
      loading: false,
      role: localStorage.getItem("role") || null,
      total: 0,
      totalCustomer: 0,
      dropId: null,
      confirmOpen: false,
      errors: null,
      snackbar: false,
      successMsg: "This customer has ongoing projects. Can not delete",
      defaultImage: require("@/assets/img/avatar.svg"),
      headers: [
        { text: "", value: "customer_profile", align: "center" },
        { text: "Customer Code", value: "customer_code", align: "start" },
        { text: "Customer Name", value: "name", align: "start" },
        { text: "Phone", value: "phone" },
        { text: "Email", value: "email", align: "start" },
        { text: "Mobile", value: "mobile", align: "start" },
        { text: "Address", value: "city" },
        { text: "Action", value: "actions", align: "end" },
      ],
    };
  },
  computed: {
    ...mapGetters({
      profile: "profile",
      hasPermission: "hasPermission",
    }),
  },
  mounted() {
    this.$store.dispatch("auth/getProfile");
    this.getCustomer();
  },

  watch: {
    search() {
      this.page = 1;
      this.getCustomer();
    },
    page() {
      this.getCustomer();
    },
  },

  methods: {
    getCustomer() {
      this.loading = true;
      CustomerService.getCustomerList(this.search, this.page)
        .then((response) => {
          this.loading = false;
          if (response.data.data) {
            this.customers = response.data.data;
            this.total = response.data.meta.last_page || 0;
            this.totalCustomer = response.data.meta.total || 0;
          }
        })
        .catch((error) => {
          this.loading = false;
          this.customers = [];
          this.total = 0;
          let data = error.response.data;
          let errorOutput = checkError.errorFormatting(data);
          this.errors = errorOutput;
        });
    },
    stringFormatting(str) {
      return str.replace(/_/g, " ");
    },
    getStatusColor(status) {
      let bgColor = "";

      switch (true) {
        case status === "ACTIVE":
          bgColor = "#A5D6A7";
          break;
        default:
          bgColor = "#EF9A9A";
          break;
      }

      return bgColor;
    },

    searchChange(value) {
      this.search = value;
    },

    canCreateEdit() {
      if (
        this.role === "ADMINISTRATOR" ||
        this.role === "MANAGER" ||
        this.role === "USER"
      ) {
        return true;
      } else {
        return false;
      }
    },

    dropMemberConfirm(uuid) {
      this.dropId = uuid;
      this.confirmOpen = true;
    },

    cancelConfirm() {
      this.confirmOpen = false;
    },

    confirm() {
      this.confirmOpen = false;
      this.dropMember();
    },

    async dropMember() {
      try {
        this.loading = true;
        await CustomerService.dropMember(this.dropId);

        this.customers = this.customers.filter((c) => c.uuid !== this.dropId);
        this.successMsg = "Customer deleted successfully!";
        this.snackbar = true;
      } catch (error) {
        let data = error.response?.data?.error || "Unknown error";
        let errorOutput = checkError.errorFormatting(data);

        let errorsArray = [];
        if (Array.isArray(errorOutput)) {
          errorsArray = errorOutput.filter((e) => !!e && e.trim());
          if (errorsArray.length === 0) errorsArray = [data];
        } else if (errorOutput && errorOutput.trim()) {
          errorsArray = [errorOutput];
        } else {
          errorsArray = [data];
        }
        this.errors = errorsArray;
      } finally {
        this.loading = false;
        this.confirmOpen = false;
      }
    },

    onDismissed() {
      this.errors = null;
    },
    closeSnackbar() {
      this.snackbar = false;
    },
  },
};
</script>
